/**
 * Free Gift Picker — Web Component (multi-slot dropdown picker)
 * Handles N slots × M fields per slot: one dropdown per size option
 * (e.g. Deckengröße + Kissengröße) plus an optional color dropdown.
 * Driven by `custom.muttertag_offer` product metafield (configured in free-gift-picker.liquid).
 *
 * Color is modeled as separate sibling products (each owns the full set of
 * size variants), so the color dropdown selects the product and the size
 * dropdowns select the variant within it. Variant titles are the size combo
 * only, e.g. "135 x 200 / 80 x 80" — joined in product option order.
 */
class FreeGiftPicker extends HTMLElement {
  connectedCallback() {
    this._sectionId = this.getAttribute('data-section-id');
    this._productFormId = this.getAttribute('data-product-form-id');
    this._mode = this.getAttribute('data-mode');
    this._slots = [];
    this._pendingGifts = null;

    var dataEl = document.getElementById('free-gift-data-' + this._sectionId);
    if (!dataEl) return;

    try {
      this._data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[FreeGiftPicker] Failed to parse JSON data', e);
      return;
    }

    // Offer ownership tag. Every gift this picker adds carries `_offer_id` so the
    // cart sync, the campaign cart module, and the pricing Function can each tell
    // WHICH offer created the line and only touch their own (see GIFT-OFFERS.md).
    // Blank when the offer metafield has no id -> legacy untagged behaviour.
    this._offerId = this._data.offerId || null;

    // Optional link gate. When the offer config sets `link`, the offer is
    // shown/added ONLY to visitors who arrived via that campaign link (cozy_offer
    // cookie). Null/blank -> site-wide for everyone (the default today). Flipping
    // this one config value is how the offer becomes link-only later — no code.
    this._requiresLink = this._data.requiresLink || null;

    this._form = document.getElementById(this._productFormId);
    if (!this._form) return;

    this._variantInput = this._form.querySelector('input[name="id"]');
    if (!this._variantInput) return;

    this._errorEl = document.getElementById('free-gift-error-' + this._sectionId);

    // Build per-slot state
    this._initSlots();

    if (this._slots.length === 0) return;

    // Link gate: if this offer is link-only and the visitor didn't arrive via the
    // matching campaign link, stay hidden and add nothing (ATC proceeds normally,
    // since _handleFormSubmit bails when the element is hidden). Site-wide when
    // _requiresLink is null.
    if (this._requiresLink) {
      var activeLink = (typeof window.cozyActiveOffer === 'function') ? window.cozyActiveOffer() : null;
      if (activeLink !== this._requiresLink) return;
    }

    this.style.display = '';

    // Bind event handlers
    this._boundHandleFormSubmit = this._handleFormSubmit.bind(this);
    this._boundHandleVariantAdded = this._handleVariantAdded.bind(this);
    this._boundHandleVariantEvent = this._handleMainVariantChange.bind(this);
    this._boundCloseOnOutside = this._closeOnOutside.bind(this);

    this._form.addEventListener('submit', this._boundHandleFormSubmit, true);
    document.addEventListener('click', this._boundCloseOnOutside);
    document.addEventListener('variant:change', this._boundHandleVariantEvent);

    this._observer = new MutationObserver(this._handleMainVariantChange.bind(this));
    this._observer.observe(this._variantInput, { attributes: true, attributeFilter: ['value'] });

    // Initial sync for read-only (size_sync_from_main) slots
    this._handleMainVariantChange();
  }

  disconnectedCallback() {
    if (this._form) this._form.removeEventListener('submit', this._boundHandleFormSubmit, true);
    if (this._boundCloseOnOutside) document.removeEventListener('click', this._boundCloseOnOutside);
    if (this._boundHandleVariantEvent) document.removeEventListener('variant:change', this._boundHandleVariantEvent);
    if (this._observer) { this._observer.disconnect(); this._observer = null; }

    for (var i = 0; i < this._slots.length; i++) {
      this._unbindSlot(this._slots[i]);
    }
  }

  _initSlots() {
    var slotDataArr = this._data.slots || [];
    for (var i = 0; i < slotDataArr.length; i++) {
      var slotData = slotDataArr[i];
      var slotEl = this.querySelector('[data-slot="' + slotData.index + '"]');
      if (!slotEl) continue;

      var slot = {
        index: slotData.index,
        giftHandle: slotData.giftHandle,
        quantity: slotData.quantity || 1,
        sizeSyncFromMain: slotData.sizeSyncFromMain || false,
        colorOptionName: slotData.colorOptionName || 'Farbe',
        hasColor: slotData.hasColor || false,
        sizeOptionNames: slotData.sizeOptionNames || [],
        colors: slotData.colors || [],
        variantMap: slotData.variantMap || {},
        selectedColorIndex: null,
        // Read-only sync slots keep a single synthetic size value.
        syncSize: null,
        // descriptors: one per size dropdown + optional color dropdown
        dropdowns: []
      };

      // Build a descriptor per size dropdown (skipped for sync slots, which
      // render a single read-only trigger with no .gift-dropdown wrapper).
      var sizeWrappers = slotEl.querySelectorAll('.gift-dropdown[data-field="size"]');
      for (var s = 0; s < sizeWrappers.length; s++) {
        var sizeWrapper = sizeWrappers[s];
        var sizeTrigger = sizeWrapper.querySelector('.gift-dropdown__trigger');
        var sizeList = sizeWrapper.querySelector('.gift-dropdown__list');
        if (!sizeTrigger || !sizeList) continue;
        var sizeDesc = {
          kind: 'size',
          sizeIndex: parseInt(sizeWrapper.getAttribute('data-size-index'), 10) || 0,
          optionName: sizeWrapper.getAttribute('data-option-name') || '',
          trigger: sizeTrigger,
          list: sizeList,
          placeholder: sizeTrigger.getAttribute('data-placeholder') || '',
          selectedValue: null,
          selectedImage: null,
          _boundToggle: null,
          _boundKeydown: null,
          _boundClick: null
        };
        this._bindDropdown(slot, sizeDesc);
        slot.dropdowns.push(sizeDesc);
      }

      // Build the color descriptor (server renders only the empty list container).
      if (slot.hasColor) {
        var colorWrapper = slotEl.querySelector('.gift-dropdown[data-field="color"]');
        if (colorWrapper) {
          var colorTrigger = colorWrapper.querySelector('.gift-dropdown__trigger');
          var colorList = colorWrapper.querySelector('.gift-dropdown__list');
          if (colorTrigger && colorList) {
            var colorDesc = {
              kind: 'color',
              trigger: colorTrigger,
              list: colorList,
              placeholder: colorTrigger.getAttribute('data-placeholder') || '',
              _boundToggle: null,
              _boundKeydown: null,
              _boundClick: null
            };
            this._populateColorOptions(slot, colorDesc);
            this._bindDropdown(slot, colorDesc);
            slot.dropdowns.push(colorDesc);
          }
        }
      }

      // Cache the read-only sync trigger, if any.
      if (slot.sizeSyncFromMain) {
        slot.syncTrigger = slotEl.querySelector('.gift-dropdown__trigger--readonly');
      }

      this._slots.push(slot);
    }
  }

  _bindDropdown(slot, desc) {
    desc._boundToggle = this._toggleList.bind(this, slot, desc);
    desc._boundKeydown = this._handleKeydown.bind(this, slot, desc);
    desc._boundClick = this._handleListClick.bind(this, slot, desc);
    desc.trigger.addEventListener('click', desc._boundToggle);
    desc.trigger.addEventListener('keydown', desc._boundKeydown);
    desc.list.addEventListener('click', desc._boundClick);
    desc.list.addEventListener('keydown', desc._boundKeydown);
  }

  _unbindSlot(slot) {
    for (var i = 0; i < slot.dropdowns.length; i++) {
      var desc = slot.dropdowns[i];
      if (desc._boundToggle) desc.trigger.removeEventListener('click', desc._boundToggle);
      if (desc._boundKeydown) desc.trigger.removeEventListener('keydown', desc._boundKeydown);
      if (desc._boundClick) desc.list.removeEventListener('click', desc._boundClick);
      if (desc._boundKeydown) desc.list.removeEventListener('keydown', desc._boundKeydown);
    }
  }

  _populateColorOptions(slot, colorDesc) {
    for (var i = 0; i < slot.colors.length; i++) {
      var color = slot.colors[i];
      var li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.setAttribute('tabindex', '-1');
      li.className = 'gift-dropdown__option';
      li.dataset.index = String(i);
      li.innerHTML =
        '<img class="gift-dropdown__option-img" src="' + color.swatch + '" alt="" width="36" height="36" loading="lazy">' +
        '<span>' + color.name + '</span>';
      colorDesc.list.appendChild(li);
    }
  }

  /* ---- size selection helpers -------------------------------------------- */

  // Array of selected size values indexed by sizeIndex (null where unchosen).
  _sizeSelections(slot) {
    var sels = [];
    for (var i = 0; i < slot.dropdowns.length; i++) {
      var d = slot.dropdowns[i];
      if (d.kind === 'size') sels[d.sizeIndex] = d.selectedValue;
    }
    return sels;
  }

  // The variant map to evaluate against: the selected color's variants, else
  // the first color's (same size structure across colors), else the flat map.
  _candidateVariantMap(slot) {
    if (!slot.hasColor) return slot.variantMap;
    if (slot.selectedColorIndex !== null && slot.colors[slot.selectedColorIndex]) {
      return slot.colors[slot.selectedColorIndex].variants || {};
    }
    if (slot.colors.length && slot.colors[0].variants) return slot.colors[0].variants;
    return {};
  }

  // Does an available variant exist with `value` at `optionIndex`, matching
  // earlier size selections? Later fields should not hide earlier choices,
  // because changing an earlier choice is how shoppers recover into another
  // valid combination.
  _comboExists(slot, vmap, optionIndex, value) {
    var sels = this._sizeSelections(slot);
    var keys = Object.keys(vmap);
    for (var i = 0; i < keys.length; i++) {
      var parts = keys[i].split(' / ');
      if (parts[optionIndex] !== value) continue;
      var ok = true;
      for (var k = 0; k < optionIndex; k++) {
        if (sels[k] != null && parts[k] !== sels[k]) { ok = false; break; }
      }
      if (ok && vmap[keys[i]] && vmap[keys[i]].available) return true;
    }
    return false;
  }

  // Full size key in option order, or null if any size option is unchosen.
  _buildSizeKey(slot) {
    var sels = this._sizeSelections(slot);
    var parts = [];
    for (var i = 0; i < sels.length; i++) {
      if (sels[i] == null) return null;
      parts.push(sels[i]);
    }
    return parts.length ? parts.join(' / ') : null;
  }

  _setOptionVisibility(option, isVisible) {
    option.hidden = !isVisible;
    if (isVisible) {
      option.style.display = '';
      option.removeAttribute('aria-hidden');
      option.removeAttribute('aria-disabled');
    } else {
      option.style.display = 'none';
      option.setAttribute('aria-hidden', 'true');
      option.setAttribute('aria-disabled', 'true');
    }
  }

  _clearAllSizes(slot) {
    for (var i = 0; i < slot.dropdowns.length; i++) {
      var desc = slot.dropdowns[i];
      if (desc.kind !== 'size' || desc.selectedValue == null) continue;
      desc.selectedValue = null;
      desc.selectedImage = null;
      this._renderSizeSelected(desc);
      var selected = desc.list.querySelectorAll('.gift-dropdown__option[aria-selected="true"]');
      for (var s = 0; s < selected.length; s++) selected[s].setAttribute('aria-selected', 'false');
    }
  }

  // Re-evaluate which size values (and colors) are still selectable, and clear
  // any prior selection that became invalid.
  _refreshAvailability(slot) {
    var vmap = this._candidateVariantMap(slot);
    var shouldFilterSizes = !slot.hasColor || slot.selectedColorIndex !== null;

    for (var d = 0; d < slot.dropdowns.length; d++) {
      var desc = slot.dropdowns[d];
      if (desc.kind !== 'size') continue;

      var options = desc.list.querySelectorAll('.gift-dropdown__option');
      for (var o = 0; o < options.length; o++) {
        var option = options[o];
        var value = option.dataset.size;
        var available = !shouldFilterSizes || this._comboExists(slot, vmap, desc.sizeIndex, value);

        if (available) {
          this._setOptionVisibility(option, true);
        } else {
          this._setOptionVisibility(option, false);
          if (option.getAttribute('aria-selected') === 'true') {
            option.setAttribute('aria-selected', 'false');
            desc.selectedValue = null;
            desc.selectedImage = null;
            this._resetTrigger(desc);
          }
        }
      }
    }

    if (slot.hasColor) this._updateColorAvailability(slot);
  }

  _resetTrigger(desc) {
    var selectedDisplay = desc.trigger.querySelector('.gift-dropdown__selected');
    if (selectedDisplay) selectedDisplay.textContent = desc.placeholder;
  }

  // Render a size dropdown's trigger: thumbnail + value, value-only, or placeholder.
  _renderSizeSelected(desc) {
    var display = desc.trigger.querySelector('.gift-dropdown__selected');
    if (!display) return;
    if (desc.selectedValue == null) { display.textContent = desc.placeholder; return; }
    if (desc.selectedImage) {
      display.innerHTML =
        '<img class="gift-dropdown__selected-img" src="' + desc.selectedImage + '" alt="" width="28" height="28">' +
        '<span>' + desc.selectedValue + '</span>';
    } else {
      display.textContent = desc.selectedValue;
    }
  }

  // Thumbnail URL for a size value in the currently selected color, or null.
  _colorImage(slot, optionName, value) {
    if (slot.selectedColorIndex === null) return null;
    var color = slot.colors[slot.selectedColorIndex];
    if (!color || !color.optionImages) return null;
    var map = color.optionImages[optionName];
    return map && map[value] ? map[value] : null;
  }

  // Add/update/remove the thumbnail <img> inside an option <li>.
  _setOptionImg(li, url) {
    var img = li.querySelector('.gift-dropdown__option-img');
    if (url) {
      if (!img) {
        img = document.createElement('img');
        img.className = 'gift-dropdown__option-img';
        img.alt = '';
        img.width = 36;
        img.height = 36;
        img.loading = 'lazy';
        li.insertBefore(img, li.firstChild);
      }
      if (img.getAttribute('src') !== url) img.setAttribute('src', url);
    } else if (img) {
      img.parentNode.removeChild(img);
    }
  }

  // Repaint every size dropdown's thumbnails (list + selected trigger) for the chosen color.
  _applyColorThumbnails(slot) {
    if (!slot.hasColor || slot.selectedColorIndex === null) return;
    var color = slot.colors[slot.selectedColorIndex];
    var optionImages = color && color.optionImages ? color.optionImages : {};

    for (var i = 0; i < slot.dropdowns.length; i++) {
      var desc = slot.dropdowns[i];
      if (desc.kind !== 'size') continue;
      var map = optionImages[desc.optionName] || null;

      var options = desc.list.querySelectorAll('.gift-dropdown__option');
      for (var o = 0; o < options.length; o++) {
        var li = options[o];
        this._setOptionImg(li, map ? (map[li.dataset.size] || null) : null);
      }

      if (desc.selectedValue != null) {
        desc.selectedImage = map ? (map[desc.selectedValue] || null) : null;
        this._renderSizeSelected(desc);
      }
    }
  }

  _colorDesc(slot) {
    for (var i = 0; i < slot.dropdowns.length; i++) {
      if (slot.dropdowns[i].kind === 'color') return slot.dropdowns[i];
    }
    return null;
  }

  // Farbe is the primary choice and stays freely switchable. A color is hidden
  // ONLY when it is fully sold out (no available variant at all) — never because the
  // currently-selected sizes happen to be OOS in it. When the color changes,
  // _refreshAvailability re-filters the size dropdowns and clears any size that is
  // out of stock in the new color.
  _updateColorAvailability(slot) {
    var colorDesc = this._colorDesc(slot);
    if (!colorDesc) return;
    var options = colorDesc.list.querySelectorAll('.gift-dropdown__option');

    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      var idx = parseInt(option.dataset.index, 10);
      var color = slot.colors[idx];
      if (!color) continue;

      var anyAvailable = false;
      var keys = Object.keys(color.variants || {});
      for (var ki = 0; ki < keys.length; ki++) {
        if (color.variants[keys[ki]] && color.variants[keys[ki]].available) { anyAvailable = true; break; }
      }

      var nameSpan = option.querySelector('span');
      if (anyAvailable) {
        this._setOptionVisibility(option, true);
        if (nameSpan) nameSpan.textContent = color.name;
      } else {
        this._setOptionVisibility(option, false);
        if (nameSpan) nameSpan.textContent = color.name;
        if (option.getAttribute('aria-selected') === 'true') {
          option.setAttribute('aria-selected', 'false');
          slot.selectedColorIndex = null;
          this._resetTrigger(colorDesc);
        }
      }
    }
  }

  /* ---- dropdown open/close/select ---------------------------------------- */

  _toggleList(slot, desc) {
    var isOpen = desc.trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) this._closeList(desc);
    else this._openList(slot, desc);
  }

  _openList(slot, desc) {
    // Close every other open list across all slots.
    for (var i = 0; i < this._slots.length; i++) {
      var dds = this._slots[i].dropdowns;
      for (var j = 0; j < dds.length; j++) {
        if (dds[j] !== desc && dds[j].trigger.getAttribute('aria-expanded') === 'true') {
          this._closeList(dds[j]);
        }
      }
    }
    desc.trigger.setAttribute('aria-expanded', 'true');
    desc.list.hidden = false;
    var target = desc.list.querySelector('[aria-selected="true"]')
      || desc.list.querySelector('.gift-dropdown__option:not([hidden]):not([aria-disabled="true"])');
    if (target) target.focus();
  }

  _closeList(desc) {
    desc.trigger.setAttribute('aria-expanded', 'false');
    desc.list.hidden = true;
  }

  _handleListClick(slot, desc, event) {
    var option = event.target.closest('.gift-dropdown__option');
    if (!option || option.getAttribute('aria-disabled') === 'true') return;
    this._selectOption(slot, desc, option);
  }

  _selectOption(slot, desc, option) {
    var options = desc.list.querySelectorAll('.gift-dropdown__option');
    for (var i = 0; i < options.length; i++) {
      options[i].setAttribute('aria-selected', 'false');
    }
    option.setAttribute('aria-selected', 'true');

    var selectedDisplay = desc.trigger.querySelector('.gift-dropdown__selected');

    if (desc.kind === 'size') {
      desc.selectedValue = option.dataset.size;
      // Color gifts: thumbnail comes from the chosen color; else from server-rendered data-image.
      desc.selectedImage = slot.hasColor
        ? this._colorImage(slot, desc.optionName, desc.selectedValue)
        : (option.dataset.image || null);
      this._renderSizeSelected(desc);
      this._refreshAvailability(slot);
    } else {
      var index = parseInt(option.dataset.index, 10);
      slot.selectedColorIndex = index;
      var color = slot.colors[index];
      if (color && selectedDisplay) {
        selectedDisplay.innerHTML =
          '<img class="gift-dropdown__selected-img" src="' + color.swatch + '" alt="" width="28" height="28">' +
          '<span>' + color.name + '</span>';
      }
      // Farbe is the primary choice: changing it resets the size picks so the
      // size dropdowns always show the correct in-stock options for the new color.
      this._clearAllSizes(slot);
      this._refreshAvailability(slot);
      this._applyColorThumbnails(slot);
    }

    this._closeList(desc);
    desc.trigger.focus();
    if (this._errorEl) this._errorEl.hidden = true;
  }

  /* ---- main-variant sync (read-only slots) ------------------------------- */

  _getSyncedSize(slot) {
    var currentVariantId = this._variantInput.value;
    var variantTitle = this._data.pillow && this._data.pillow.variants[currentVariantId];
    if (!variantTitle) return null;
    return variantTitle.split(' / ')[0] || variantTitle;
  }

  _syncFromMainVariant(slot) {
    if (!slot.sizeSyncFromMain || !slot.syncTrigger) return;
    var sizeVal = this._getSyncedSize(slot);
    if (!sizeVal) return;
    slot.syncSize = sizeVal;
    var selectedDisplay = slot.syncTrigger.querySelector('.gift-dropdown__selected');
    if (selectedDisplay) selectedDisplay.textContent = sizeVal;
    if (slot.hasColor) this._updateColorAvailability(slot);
  }

  _handleMainVariantChange() {
    for (var i = 0; i < this._slots.length; i++) {
      var slot = this._slots[i];
      if (slot.sizeSyncFromMain) {
        this._syncFromMainVariant(slot);
      } else if (slot.hasColor) {
        this._refreshAvailability(slot);
      }
    }
  }

  /* ---- keyboard / outside click ------------------------------------------ */

  _handleKeydown(slot, desc, event) {
    var isOpen = desc.trigger.getAttribute('aria-expanded') === 'true';

    if (event.key === 'Escape') {
      this._closeList(desc);
      desc.trigger.focus();
      event.preventDefault();
      return;
    }

    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
      this._openList(slot, desc);
      event.preventDefault();
      return;
    }

    if (isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      var options = Array.from(desc.list.querySelectorAll('.gift-dropdown__option:not([hidden]):not([aria-disabled="true"])'));
      var current = document.activeElement;
      var idx = options.indexOf(current);
      if (event.key === 'ArrowDown') idx = idx < options.length - 1 ? idx + 1 : 0;
      else idx = idx > 0 ? idx - 1 : options.length - 1;
      if (options[idx]) options[idx].focus();
      event.preventDefault();
      return;
    }

    if (isOpen && (event.key === 'Enter' || event.key === ' ')) {
      var focused = document.activeElement;
      if (focused && focused.classList.contains('gift-dropdown__option')) {
        this._selectOption(slot, desc, focused);
        event.preventDefault();
      }
    }
  }

  _closeOnOutside(event) {
    if (this.contains(event.target)) return;
    for (var i = 0; i < this._slots.length; i++) {
      var dds = this._slots[i].dropdowns;
      for (var j = 0; j < dds.length; j++) this._closeList(dds[j]);
    }
  }

  /* ---- submit / cart add ------------------------------------------------- */

  _firstSizeTrigger(slot) {
    for (var i = 0; i < slot.dropdowns.length; i++) {
      if (slot.dropdowns[i].kind === 'size') return slot.dropdowns[i].trigger;
    }
    return null;
  }

  _getMainQuantity() {
    var quantityInput = this._form ? this._form.querySelector('[name="quantity"]') : null;
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  _handleFormSubmit(event) {
    // COZY-103: if this picker has been hidden at runtime — typically by the
    // cozy-traffic-gate.js email-source gate (data-hide-on-email-source +
    // [hidden]) — the user can't see or interact with the slots, so we must
    // not block the form. Let ATC proceed without adding any gifts.
    if (this.hidden || this.offsetParent === null) return;

    // Validate every slot: all fields chosen AND the resulting combination is an
    // in-stock variant. We never POST an unavailable variant to /cart/add.js.
    var valid = true;
    var firstInvalidTrigger = null;
    var resolved = [];
    var mainQuantity = this._getMainQuantity();

    for (var i = 0; i < this._slots.length; i++) {
      var slot = this._slots[i];
      var slotIncomplete = false;

      if (!slot.sizeSyncFromMain) {
        for (var d = 0; d < slot.dropdowns.length; d++) {
          var desc = slot.dropdowns[d];
          if (desc.kind === 'size' && desc.selectedValue === null) {
            slotIncomplete = true;
            if (!firstInvalidTrigger) firstInvalidTrigger = desc.trigger;
          }
        }
      }

      if (slot.hasColor && slot.selectedColorIndex === null) {
        slotIncomplete = true;
        var cd = this._colorDesc(slot);
        if (!firstInvalidTrigger && cd) firstInvalidTrigger = cd.trigger;
      }

      if (slotIncomplete) { valid = false; continue; }

      // All fields chosen — confirm it maps to an in-stock variant before adding.
      var giftVariant = this._resolveVariant(slot);
      var variantId = giftVariant ? giftVariant.id : null;
      if (!variantId) {
        valid = false;
        if (!firstInvalidTrigger) {
          firstInvalidTrigger = this._firstSizeTrigger(slot) || (cd && cd.trigger);
        }
        continue;
      }
      // Tag with hidden properties so cart-gift-sync.js recognises theme-added free
      // gifts (counts as gift, never as "buy" -> no loop) and `_offer_id` records the
      // owning offer so other systems never grab/cap/free a different offer's gift.
      var giftProps = { _free_gift: 'true' };
      if (this._offerId) giftProps._offer_id = this._offerId;
      resolved.push({ id: variantId, quantity: slot.quantity * mainQuantity, properties: giftProps });
    }

    if (!valid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this._errorEl) this._errorEl.hidden = false;
      if (firstInvalidTrigger) firstInvalidTrigger.focus();
      return;
    }

    this._pendingGifts = resolved;
    if (this._pendingGifts.length > 0) {
      document.addEventListener('variant:added', this._boundHandleVariantAdded, { once: true });
    }
  }

  // Resolve the currently-chosen, in-stock variant OBJECT ({ id, available })
  // for a slot, or null. _resolveVariantId delegates to this.
  _resolveVariant(slot) {
    var vmap;

    if (slot.hasColor) {
      if (slot.selectedColorIndex === null || !slot.colors[slot.selectedColorIndex]) return null;
      vmap = slot.colors[slot.selectedColorIndex].variants || {};
    } else {
      vmap = slot.variantMap || {};
    }

    // Read-only sync slots: single value, keep prefix fallback for legacy multi-option products.
    if (slot.sizeSyncFromMain) {
      var synced = slot.syncSize || this._getSyncedSize(slot);
      if (!synced) return null;
      var direct = vmap[synced];
      if (direct) return direct.available ? direct : null;
      var skeys = Object.keys(vmap);
      for (var si = 0; si < skeys.length; si++) {
        if (skeys[si] === synced || skeys[si].indexOf(synced + ' / ') === 0) {
          return vmap[skeys[si]].available ? vmap[skeys[si]] : null;
        }
      }
      return null;
    }

    var key = this._buildSizeKey(slot);
    var v = key ? vmap[key] : null;
    // Only resolve to an in-stock variant — never add an unavailable item to cart.
    if (v && v.available) return v;
    return null;
  }

  _resolveVariantId(slot) {
    var v = this._resolveVariant(slot);
    return v ? v.id : null;
  }

  _handleVariantAdded() {
    if (!this._pendingGifts || this._pendingGifts.length === 0) return;

    var gifts = this._pendingGifts;
    this._pendingGifts = null;

    var cartUrl = (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.cartAddUrl)
      ? window.themeVariables.routes.cartAddUrl
      : '/cart/add.js';

    // Use Shopify's array form to add all gifts in one request
    fetch(cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: gifts })
    })
    .then(function (response) {
      if (!response.ok) throw new Error('Failed to add gifts to cart');
      return response.json();
    })
    .then(function () {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      var miniCart = document.getElementById('mini-cart');
      if (miniCart && miniCart.open !== undefined) miniCart.open = true;
    })
    .catch(function (err) {
      console.error('[FreeGiftPicker]', err);
    });
  }
}

if (!customElements.get('free-gift-picker')) {
  customElements.define('free-gift-picker', FreeGiftPicker);
}
